import React, { useCallback } from "react";
import { useQuery } from "@apollo/client";
import i18n from "i18next";
import { initReactI18next, I18nextProvider } from "react-i18next";
import i18nXhrBackend from "i18next-xhr-backend";
import { BrowserRouter as Router } from "react-router-dom";

import { getContextQuery } from "./getContextQuery";
import { CenterSpinner } from "components/CenterSpinner";
import { throwOnError, throwOnNull, throwOnCondition } from "libraries/errorHelpers";
import { ApplicationContextProvider } from "./ApplicationContext";

export type WrapperProps = {
  debug: boolean;
  siteEnvironment: {
    name: string;
    maintenance: boolean;
    appEndpointCore: string;
    appEndpointStore: string;
    appEndpointOffice: string;
    appEndpointPublic: string;
    sessionCookie: string;
    sessionDomain: string;
  };
  token: string;
  setToken: (token: string, persistent: boolean) => void;
  clearToken: () => void;
};

export const Wrapper: React.FC<WrapperProps> = (props) => {
  const { debug, siteEnvironment, token, setToken, clearToken, children } = props;
  const contextQuery = getContextQuery(token);
  const { loading, error, data, refetch } = useQuery(contextQuery);

  const setSessionToken = useCallback(
    (token: string, persistent: boolean) => {
      setToken(token, persistent);
    },
    [setToken],
  );

  const clearSessionToken = useCallback(() => {
    clearToken();
  }, [clearToken]);

  const refetchSession = useCallback(() => {
    refetch();
  }, [refetch]);

  if (loading) return <CenterSpinner size="large" />;

  const subject = "Application.Wrapper";
  const message = "Can't get context";
  throwOnError(error, subject, message);
  throwOnNull(data, subject, message, "data is null");
  throwOnNull(data.environment, subject, message, "environment is null");
  throwOnNull(data.default, subject, message, "default is null");
  throwOnCondition(
    token !== undefined && token !== "" && !data.accountSession,
    subject,
    message,
    "account session not found",
  );

  const coreDefault = data.default;
  const coreEnvironment = data.environment;
  const session = data.accountSession ?? {};
  const authenticated = (token ?? false) && (session.id ?? false) && true;

  const applicationContext = {
    debug,
    coreDefault,
    coreEnvironment,
    siteEnvironment,
    session,
    authenticated,
    configuration: {
      language: session?.currentUser?.language ?? coreDefault?.language ?? "EN",
      timezone: session?.currentUser?.timezone ?? coreDefault?.timezone ?? "EUROPE_ROME",
    },
    setSessionToken,
    clearSessionToken,
    refetchSession,
  };

  i18n
    .use(i18nXhrBackend)
    .use(initReactI18next)
    .init({
      lng: applicationContext.configuration.language.toLowerCase(),
      fallbackLng: "en",
      debug,
      interpolation: {
        escapeValue: false,
      },
      ns: ["globals"],
      defaultNS: "globals",
      backend: {
        loadPath: "/locales/{{lng}}/{{ns}}.json",
      },
    });

  return (
    <ApplicationContextProvider value={applicationContext}>
      <I18nextProvider i18n={i18n}>
        <Router>{children}</Router>
      </I18nextProvider>
    </ApplicationContextProvider>
  );
};
